import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UploadCloud } from 'lucide-react';
import { useCallback, useRef, useState, type DragEvent } from 'react';

interface Props {
    onFileSelected: (file: File) => void;
    accept?: string;
    maxSize?: number;
    uploading?: boolean;
    label?: string;
    hint?: string;
}

export function FileUpload({ onFileSelected, accept = '*', maxSize = 20 * 1024 * 1024, uploading, label, hint }: Props) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState('');

    const handleFile = useCallback(
        (file: File) => {
            setError('');
            if (file.size > maxSize) {
                setError('Arquivo muito grande');
                return;
            }
            onFileSelected(file);
        },
        [maxSize, onFileSelected]
    );

    const handleDrop = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) {
                handleFile(file);
            }
        },
        [handleFile]
    );

    return (
        <Box>
            <Box
                onDrop={handleDrop}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => inputRef.current?.click()}
                sx={{
                    border: '2px dashed',
                    borderColor: dragOver ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    bgcolor: dragOver ? 'action.hover' : 'background.paper',
                    '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' }
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                            handleFile(f);
                        }
                        e.target.value = '';
                    }}
                />
                <UploadCloud size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
                <Typography variant="body2" color="text.secondary">
                    {uploading ? 'Enviando...' : (label ?? 'Arraste o arquivo ou clique para selecionar')}
                </Typography>
                {hint && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                        {hint}
                    </Typography>
                )}
            </Box>
            {error && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}
